import React, { ReactElement } from "react";
import Link from "next/link";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import { VideoOverlayTrigger } from "../videoOverlayTrigger/videoOverlayTrigger";
import styles from "./videoTestimonials.module.scss";
import { ArrowLeft, ArrowRight } from "@untitled-ui/icons-react";
import classNames from "classnames";

export const VideoTestimonials = (props: Props): ReactElement => {
  const BadgeIcon = props.badgeIcon;

  const PrimaryActionIcon = props.primaryActionIcon;
  const SecondaryActionIcon = props.secondaryActionIcon;

  return (
    <section className={styles.base} id={props.id}>
      <div className={styles.wrapper}>
        <div className={styles.headingAndActions}>
          <div className={styles.texts}>
            <div className={styles.badge}>
              <BadgeIcon />
              <span className={styles.badgeText}>{props.badgeText}</span>
            </div>
            <p className={styles.title}>{props.title}</p>
          </div>
          <div className={styles.actions}>
            {props.secondaryActionUrl && (
              <Link
                href={props.secondaryActionUrl}
                className={styles.secondaryActionUrl}
              >
                <SecondaryActionIcon viewBox="0 0 24 24" />
                {props.secondaryActionTitle}
              </Link>
            )}
            {props.primaryActionUrl && (
              <Link
                href={props.primaryActionUrl}
                className={styles.primaryActionUrl}
              >
                <PrimaryActionIcon viewBox="0 0 24 24" />
                {props.primaryActionTitle}
              </Link>
            )}
          </div>
        </div>
        <p className={styles.supportingText}>{props.supportingText}</p>
        <div className={styles.mobileActions}>
          {props.secondaryActionUrl && (
            <Link
              href={props.secondaryActionUrl}
              className={styles.mobileSecondaryActionUrl}
            >
              <SecondaryActionIcon viewBox="0 0 24 24" />
              {props.secondaryActionTitle}
            </Link>
          )}
          {props.primaryActionUrl && (
            <Link
              href={props.primaryActionUrl}
              className={styles.mobilePrimaryActionUrl}
            >
              <PrimaryActionIcon viewBox="0 0 24 24" />
              {props.primaryActionTitle}
            </Link>
          )}
        </div>
      </div>
      <div className={styles.carouselWrapper}>
        <Swiper
          freeMode
          spaceBetween={32}
          slidesPerView={"auto"}
          loop={props.items && props.items.length > 1}
          threshold={10}
        >
          {props.items?.map((item, i) => (
            <SwiperSlide key={`slide_${i}`} style={{ width: 270 }}>
              <VideoTestimonialCard key={`card${i}`} {...item} />
            </SwiperSlide>
          ))}
          {props.items?.length > 1 && (
            <div className={styles.prevNextWrapper}>
              <PrevButton />
              <NextButton />
            </div>
          )}
        </Swiper>
      </div>
    </section>
  );
};

export const VideoTestimonialCard = (
  props: Props["items"][0],
): ReactElement => {
  return (
    <VideoOverlayTrigger
      triggerNode={
        <div className={classNames(styles.cardWrapper, props.className)}>
          <div
            className={styles.cardContent}
            style={{
              backgroundImage: `url(${props.thumbnail})`,
            }}
          >
            <PlayButton className={styles.playButton} />
            {(props.title || props.desc) && (
              <div className={styles.cardLabel}>
                {props.title && (
                  <p className={styles.cardTitle}>{props.title}</p>
                )}

                {props.desc && <p className={styles.cardDesc}>{props.desc}</p>}
              </div>
            )}
          </div>
        </div>
      }
      mediaFile={props.videoUrl}
    />
  );
};

const PlayButton = (props: any) => {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M24 48C37.2548 48 48 37.2548 48 24C48 10.7452 37.2548 0 24 0C10.7452 0 0 10.7452 0 24C0 37.2548 10.7452 48 24 48ZM20.25 32.8033L33.75 25.2576C34.75 24.6987 34.75 23.3013 33.75 22.7424L20.25 15.1967C19.25 14.6378 18 15.3365 18 16.4543V31.5457C18 32.6635 19.25 33.3622 20.25 32.8033Z"
        fill="white"
      />
    </svg>
  );
};

const PrevButton = () => {
  const swiper = useSwiper();

  return (
    <button
      className={styles.prevNextButton}
      onClick={() => swiper.slidePrev()}
    >
      <ArrowLeft />
    </button>
  );
};

const NextButton = () => {
  const swiper = useSwiper();

  return (
    <button
      className={styles.prevNextButton}
      onClick={() => swiper.slideNext()}
    >
      <ArrowRight />
    </button>
  );
};

interface Props {
  badgeText: string;
  badgeIcon: React.ElementType;
  title: string;
  supportingText: string;
  secondaryActionTitle?: string;
  secondaryActionUrl?: string;
  secondaryActionIcon: React.ElementType;
  primaryActionTitle?: string;
  primaryActionUrl?: string;
  primaryActionIcon: React.ElementType;
  id?: string;
  items: {
    thumbnail: string;
    title: string;
    desc: string;
    videoUrl: string;
    className?: string;
  }[];
}
